/**
 * Customer-friendly topic groupings for the IT Act mapper.
 *
 * The raw mapping data has 15 internal categories ("TDS", "TCS",
 * "Deductions", "Tax Regimes", "Trusts & NGOs", etc.) which is great for tax
 * professionals but overwhelming for everyday users. We collapse those into
 * 6 buckets that match how a salaried/self-employed customer actually thinks
 * ("save tax", "salary & HRA", "property", etc.) plus an implicit "all".
 *
 * Each topic exposes a `match(section)` predicate so we can derive the
 * counts and the filtered list without keeping a duplicate index in sync.
 */
import type { Section } from './types';
import {
  PiggyBank, Briefcase, TrendingUp, Home, Receipt, Building2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface Topic {
  id:          string;
  label:       string;
  description: string;
  Icon:        LucideIcon;
  match:       (s: Section) => boolean;
}

export const TOPICS: Topic[] = [
  {
    id:          'save-tax',
    label:       'Save Tax',
    description: 'Deductions like 80C, 80D, NPS',
    Icon:        PiggyBank,
    match:       (s) => s.category === 'Deductions',
  },
  {
    id:          'salary',
    label:       'Salary & HRA',
    description: 'Salary, HRA, gratuity, regimes',
    Icon:        Briefcase,
    match:       (s) => s.category === 'Salary' || s.category === 'Tax Regimes',
  },
  {
    id:          'capital-gains',
    label:       'Capital Gains',
    description: 'Stocks, mutual funds, LTCG, STCG',
    Icon:        TrendingUp,
    match:       (s) => s.category === 'Capital Gains',
  },
  {
    id:          'property',
    label:       'Property & Rent',
    description: 'Home loan, rental income',
    Icon:        Home,
    match:       (s) => s.category === 'House Property',
  },
  {
    id:          'tds',
    label:       'TDS & TCS',
    description: 'Tax deducted at source',
    Icon:        Receipt,
    match:       (s) => s.category === 'TDS' || s.category === 'TCS',
  },
  {
    id:          'business',
    label:       'Business & Audit',
    description: 'Business income, audit, compliance',
    Icon:        Building2,
    match:       (s) =>
      s.category === 'Business Income' ||
      s.category === 'Audit' ||
      s.category === 'Assessment & Compliance',
  },
];

/** Returns the active topic given an id, or null. */
export function findTopic(id: string | null): Topic | null {
  if (!id) return null;
  return TOPICS.find((t) => t.id === id) ?? null;
}
